/**
 * Blog Post: 首次置業按揭全攻略：從申請到批核完整流程 [2026版]
 * First-Time Homebuyer Mortgage Guide: Complete Process from Application to Approval [2026]
 */
import { useEffect } from 'react';
import { useCustomTranslation } from '../hooks';
import { Box, Container, Typography } from '@mui/material';
import { trackPageView } from '../utils/analytics';
import AmazonRecommendation from '../components/common/AmazonRecommendation';
import SEO from '../components/common/SEO';

export default function BlogPost3() {
  const { i18n } = useCustomTranslation();
  const isEnglish = i18n.language === 'en';

  useEffect(() => {
    trackPageView('/blog/first-time-homebuyer-mortgage-guide-2026', 'Blog Post 3');
    document.title = isEnglish
      ? 'First-Time Homebuyer Mortgage Guide: Complete Application Process [2026] | PayWise365'
      : '首次置業按揭全攻略：從申請到批核完整流程 [2026版] | PayWise365';
  }, [isEnglish]);

  const seoTitle = isEnglish
    ? 'First-Time Homebuyer Mortgage Guide: Complete Application Process [2026] | PayWise365'
    : '首次置業按揭全攻略：從申請到批核完整流程 [2026版] | PayWise365';

  const seoDescription = isEnglish
    ? 'Complete 2026 guide for first-time homebuyers in Hong Kong. Step-by-step mortgage application process, required documents, eligibility criteria, government schemes, and expert tips to secure the best mortgage deal.'
    : '2026香港首次置業者完整按揭攻略。逐步解說申請流程、所需文件、資格要求、政府資助計劃，助你成功上車並獲得最佳按揭條件。';

  const seoKeywords = isEnglish
    ? 'first time homebuyer Hong Kong, 首次置業, mortgage application guide, 按揭申請, first home mortgage HK, government housing schemes, 居屋, home ownership scheme, mortgage approval'
    : '首次置業, 按揭申請流程, 香港買樓, 上車盤, 按揭批核, 政府資助房屋, 首置印花稅';

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <SEO
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonicalUrl="https://www.paywise365.com/blog/first-time-homebuyer-mortgage-guide-2026"
        ogType="article"
        publishDate="2026-02-05T00:00:00+08:00"
        modifiedDate="2026-02-05T00:00:00+08:00"
        articleSection={isEnglish ? 'First Home Guide' : '首次置業指南'}
        tags={isEnglish ? ['First Time Buyer', 'Mortgage Application', 'Hong Kong Property'] : ['首次置業', '按揭申請', '香港樓市']}
      />
      {/* Page Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          py: { xs: 6, md: 8 },
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h2" sx={{ mb: 2, fontWeight: 700 }}>
            {isEnglish ? 'First-Time Homebuyer Mortgage Guide' : '首次置業按揭全攻略'}
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.95, fontWeight: 400 }}>
            {isEnglish ? 'Complete Application Process [2026]' : '從申請到批核完整流程 [2026版]'}
          </Typography>
        </Container>
      </Box>

      {/* Article Content */}
      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
        <Box
          component="article"
          sx={{
            lineHeight: 1.8,
            '& p': {
              mb: 2,
            },
            '& h2': {
              mt: 4,
              mb: 2,
              fontSize: '1.75rem',
              fontWeight: 700,
            },
            '& h3': {
              mt: 3,
              mb: 1.5,
              fontSize: '1.5rem',
              fontWeight: 600,
            },
            '& ul, & ol': {
              mb: 2,
              pl: 3,
            },
            '& li': {
              mb: 1,
            },
          }}
        >
          {!isEnglish ? (
            // Cantonese Content
            <>
              <div style={{ marginBottom: '30px', paddingBottom: '20px', borderBottom: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '10px' }}>
                  <span style={{
                    background: '#eff6ff',
                    color: '#2563eb',
                    padding: '6px 14px',
                    borderRadius: '14px',
                    fontWeight: '500',
                    marginRight: '12px'
                  }}>
                    首次置業
                  </span>
                  <span>發布日期：2026年1月31日</span>
                  <span style={{ margin: '0 8px' }}>•</span>
                  <span>閱讀時間：15 分鐘</span>
                </div>
              </div>

              <h2>🏠 引言：實現置業夢的第一步</h2>
              <p>
                買樓是人生重要決定，尤其對首次置業人士而言，按揭申請流程複雜，文件繁多，稍有不慎就可能影響批核結果。本文將為你詳細拆解整個按揭申請流程，從準備階段到成功批核，讓你對每個步驟都瞭如指掌，大大提高成功率。
              </p>
              <p>
                根據2026年最新政策，首次置業人士可享多項優惠，包括較高按揭成數及較低印花稅率。把握這些優惠，配合正確的申請策略，你的置業夢將更容易實現！
              </p>

              <h2>✅ 第一步：評估自己是否準備好</h2>

              <h3>1. 財務準備檢查清單</h3>

              <div style={{
                background: '#f0f9ff',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #7dd3fc',
                margin: '20px 0'
              }}>
                <h4 style={{ marginTop: 0 }}>💰 首期資金</h4>
                <ul>
                  <li><strong>私人樓宇：</strong>最少10%（HKD 1,000萬以下可申請按揭保險）</li>
                  <li><strong>居屋（白表）：</strong>最少5%</li>
                  <li><strong>居屋（綠表）：</strong>最少5%</li>
                  <li><strong>建議：</strong>準備多10-15%作為額外開支（律師費、印花稅、裝修等）</li>
                </ul>

                <h4>📊 收入要求</h4>
                <ul>
                  <li><strong>一般要求：</strong>月供不超過月入50%</li>
                  <li><strong>壓力測試：</strong>假設利率+3%，月供不超過月入60%</li>
                  <li><strong>收入證明：</strong>受薪人士需6個月薪金紀錄；自僱人士需2年財務報表</li>
                </ul>

                <h4>💳 信貸記錄</h4>
                <ul>
                  <li><strong>信貸評分：</strong>580分以上為理想（TU評分）</li>
                  <li><strong>還款紀錄：</strong>過去24個月無逾期還款</li>
                  <li><strong>信用卡使用率：</strong>建議低於50%總信用額</li>
                  <li><strong>債務比率：</strong>月供所有債務不超過月入50%</li>
                </ul>
              </div>

              <h3>2. 快速自我評估：我可以借多少？</h3>

              <div style={{
                background: '#f8fafc',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                margin: '20px 0'
              }}>
                <h4 style={{ marginTop: 0 }}>💡 簡易計算公式</h4>
                <p><strong>最高借貸額 = 月入 × 50% × 12個月 × 貸款年期 ÷ (1 + 平均利率 × 貸款年期 ÷ 2)</strong></p>

                <p style={{ marginTop: '20px' }}><strong>實際例子：</strong></p>
                <ul>
                  <li>月入：HKD 40,000</li>
                  <li>貸款年期：25年</li>
                  <li>利率：3.875%</li>
                  <li>估算可借：約 HKD 3,600,000</li>
                  <li>若首期20%，可負擔樓價：約 HKD 4,500,000</li>
                </ul>

                <p style={{
                  background: '#eff6ff',
                  padding: '15px',
                  borderRadius: '6px',
                  marginTop: '15px',
                  marginBottom: 0
                }}>
                  <strong>💻 精確計算：</strong>使用我們的<a href="/" style={{ color: '#2563eb' }}>免費按揭計算機</a>，輸入你的實際數據，即時得出精確結果！
                </p>
              </div>

              <h2>📝 第二步：準備申請文件</h2>

              <p>完整的文件準備是成功關鍵！缺少任何文件都會延誤批核，甚至被拒絕。</p>

              <h3>身份證明文件</h3>
              <ul>
                <li>✅ 香港身份證副本</li>
                <li>✅ 住址證明（3個月內的水電煤單據或銀行月結單）</li>
                <li>✅ 若非永久居民：工作簽證副本</li>
              </ul>

              <h3>收入證明文件（受薪人士）</h3>
              <ul>
                <li>✅ 最近6個月銀行月結單（顯示薪金入賬）</li>
                <li>✅ 最近3個月糧單</li>
                <li>✅ 僱傭合約副本</li>
                <li>✅ 公司信（證明職位、入職日期、薪金）</li>
                <li>✅ 最近1年稅單（IR56B）</li>
                <li>✅ 強積金供款紀錄（6個月）</li>
              </ul>

              <h3>收入證明文件（自僱人士）</h3>
              <ul>
                <li>✅ 最近2年財務報表（經審計）</li>
                <li>✅ 最近2年報稅表（已評稅的稅單）</li>
                <li>✅ 公司商業登記證副本</li>
                <li>✅ 最近6個月銀行月結單（公司及個人）</li>
                <li>✅ 會計師證明信</li>
              </ul>

              <h3>物業相關文件</h3>
              <ul>
                <li>✅ 臨時買賣合約副本</li>
                <li>✅ 物業估價報告（銀行會安排）</li>
                <li>✅ 發展商價單（一手樓）</li>
                <li>✅ 土地查冊（二手樓，律師會辦理）</li>
              </ul>

              <div style={{
                background: '#fef3c7',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #fbbf24',
                margin: '20px 0'
              }}>
                <h4 style={{ marginTop: 0 }}>💡 專業貼士</h4>
                <ul style={{ marginBottom: 0 }}>
                  <li><strong>提前準備：</strong>開始睇樓前就準備好所有文件，加快批核</li>
                  <li><strong>整齊分類：</strong>用文件夾按類別整理，方便銀行審閱</li>
                  <li><strong>數碼備份：</strong>掃描所有文件儲存於電腦，隨時可傳送</li>
                  <li><strong>多準備副本：</strong>同時申請多間銀行時需要多份副本</li>
                </ul>
              </div>

              <h2>🔟 第三步：按揭申請10大步驟</h2>

              <h3>步驟1：按揭預先批核（Pre-approval）【可選但強烈建議】</h3>
              <p><strong>時間：</strong>睇樓前 1-2週</p>
              <p><strong>做什麼：</strong></p>
              <ul>
                <li>向銀行遞交收入證明等文件</li>
                <li>銀行評估你的借貸能力</li>
                <li>獲得原則上批准（Subject to Property Valuation）</li>
              </ul>
              <p><strong>好處：</strong></p>
              <ul>
                <li>✅ 清楚知道自己的購買力</li>
                <li>✅ 議價時更有信心</li>
                <li>✅ 加快正式申請流程</li>
                <li>✅ 賣家更願意接受你的出價（證明你有能力買）</li>
              </ul>

              <h3>步驟2：物業睇樓及落訂</h3>
              <p><strong>時間：</strong>2-4週</p>
              <p><strong>做什麼：</strong></p>
              <ul>
                <li>實地視察心儀物業</li>
                <li>比較不同單位的優劣</li>
                <li>與賣家議價</li>
                <li>達成協議後支付臨時訂金（通常HKD 10萬-50萬）</li>
                <li>簽署臨時買賣合約</li>
              </ul>

              <h3>步驟3：正式遞交按揭申請</h3>
              <p><strong>時間：</strong>簽署臨約後 1-3 天內</p>
              <p><strong>做什麼：</strong></p>
              <ul>
                <li>向銀行遞交完整申請表及所有文件</li>
                <li>建議同時向3-4間銀行申請，比較條款</li>
                <li>填寫授權書，讓銀行查閱信貸報告</li>
              </ul>
              <p><strong>注意事項：</strong></p>
              <ul>
                <li>⚠️ 申請表必須填寫完整準確，任何錯漏都會延誤</li>
                <li>⚠️ 不要隱瞞任何債務或收入來源</li>
                <li>⚠️ 同時申請多間銀行，但最好在2週內完成，避免信貸查詢紀錄過多</li>
              </ul>

              <h3>步驟4：銀行物業估價</h3>
              <p><strong>時間：</strong>遞交申請後 3-5 天</p>
              <p><strong>做什麼：</strong></p>
              <ul>
                <li>銀行委託估價行評估物業價值</li>
                <li>估價員可能實地視察，也可能只憑紀錄估價</li>
                <li>估價結果決定銀行最高貸款額</li>
              </ul>
              <p><strong>潛在問題：估價不足</strong></p>
              <ul>
                <li>銀行估價低於買價，貸款額會減少</li>
                <li>買家需準備更多首期，或與賣家重新議價</li>
                <li>解決方法：向多間銀行估價，選擇估價較高的銀行</li>
              </ul>

              <h3>步驟5：按揭保險申請（如需要）</h3>
              <p><strong>時間：</strong>同步進行</p>
              <p><strong>什麼時候需要：</strong></p>
              <ul>
                <li>HKD 1,000萬以下物業，首期不足40%</li>
                <li>例如：HKD 600萬物業，想借80%（HKD 480萬），需申請按保</li>
              </ul>
              <p><strong>保費：</strong></p>
              <ul>
                <li>60%按揭：約貸款額的1.15%</li>
                <li>80%按揭：約貸款額的2.15%</li>
                <li>90%按揭：約貸款額的3.75%-4.35%</li>
                <li>可選擇一次性繳付或加入貸款額分期攤還</li>
              </ul>

              <h3>步驟6：文件審核及補充</h3>
              <p><strong>時間：</strong>遞交申請後 5-10 天</p>
              <p><strong>可能發生：</strong></p>
              <ul>
                <li>銀行職員電話聯絡，要求補充文件</li>
                <li>可能需要解釋某些收入來源或支出</li>
                <li>可能需要提供額外證明文件</li>
              </ul>
              <p><strong>應對策略：</strong></p>
              <ul>
                <li>📱 保持電話暢通，及時回覆銀行查詢</li>
                <li>📄 盡快補交所需文件，不要拖延</li>
                <li>🤝 態度友善合作，讓審批人員感受你的誠意</li>
              </ul>

              <h3>步驟7：銀行審批決定</h3>
              <p><strong>時間：</strong>遞交完整文件後 7-14 天</p>
              <p><strong>可能結果：</strong></p>
              <ol>
                <li><strong>✅ 批准（Approved）：</strong>恭喜！進入下一步</li>
                <li><strong>⚠️ 有條件批准（Conditional Approval）：</strong>需滿足某些條件才正式批准</li>
                <li><strong>❌ 拒絕（Rejected）：</strong>未能通過，需了解原因並尋求其他方案</li>
              </ol>

              <h3>步驟8：收到貸款確認書（Mortgage Offer Letter）</h3>
              <p><strong>時間：</strong>批准後 3-5 天</p>
              <p><strong>內容包括：</strong></p>
              <ul>
                <li>貸款金額</li>
                <li>利率（H按或P按）</li>
                <li>還款年期</li>
                <li>每月供款額</li>
                <li>現金回贈金額</li>
                <li>罰息期條款</li>
                <li>其他條款及細則</li>
              </ul>
              <p><strong>重要：</strong></p>
              <ul>
                <li>📖 仔細閱讀所有條款</li>
                <li>❓ 不明白的地方立即向銀行查詢</li>
                <li>🔍 比較不同銀行的offer，選擇最優惠的</li>
              </ul>

              <h3>步驟9：簽署正式買賣合約</h3>
              <p><strong>時間：</strong>簽臨約後約 14 天內</p>
              <p><strong>做什麼：</strong></p>
              <ul>
                <li>支付大訂（通常為樓價5-10%，扣除已付臨訂）</li>
                <li>雙方律師見證下簽署正式買賣合約</li>
              </ul>

              <h3>步驟10：簽署按揭契約及提款</h3>
              <p><strong>時間：</strong>成交日前 1-2 週</p>
              <p><strong>做什麼：</strong></p>
              <ul>
                <li>到銀行或律師樓簽署按揭契約</li>
                <li>銀行放款至律師託管戶口</li>
                <li>成交日律師處理款項交收</li>
                <li>取得鑰匙，正式成為業主！🎉</li>
              </ul>

              <div style={{
                background: '#f0fdf4',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #86efac',
                margin: '30px 0'
              }}>
                <h3 style={{ marginTop: 0, color: '#065f46' }}>⏱️ 整個流程時間表總結</h3>
                <ul style={{ marginBottom: 0 }}>
                  <li><strong>預先批核（可選）：</strong>1-2 週</li>
                  <li><strong>睇樓至簽臨約：</strong>2-4 週</li>
                  <li><strong>遞交申請至批核：</strong>2-3 週</li>
                  <li><strong>簽正約至成交：</strong>約 6-8 週</li>
                  <li><strong>總時間：</strong>約 2-3 個月</li>
                </ul>
              </div>

              <h2>🏛️ 第四步：了解政府資助計劃</h2>

              <h3>1. 居屋按揭（Home Ownership Scheme）</h3>
              <p><strong>白表申請人：</strong></p>
              <ul>
                <li>最高按揭成數：90%（新居屋）或 85-90%（二手居屋）</li>
                <li>需要通過入息及資產審查</li>
                <li>需要通過壓力測試</li>
                <li>利率較私樓優惠約 0.1-0.3%</li>
              </ul>

              <p><strong>綠表申請人：</strong></p>
              <ul>
                <li>最高按揭成數：95%（新居屋）</li>
                <li>無需壓力測試（政府擔保期內）</li>
                <li>供款佔入息比例只需不超過50%</li>
                <li>利率較私樓優惠</li>
              </ul>

              <p><strong>重要限制：</strong></p>
              <ul>
                <li>❌ 首 5 年禁止自由轉讓（補地價後可）</li>
                <li>❌ 轉售需付補價（市值與原價差額）</li>
                <li>❌ 政府擔保期：30年（新居屋）或 60-單位樓齡年（二手居屋）</li>
              </ul>

              <h3>2. 按揭保險計劃（Mortgage Insurance）</h3>
              <p>讓首期不足40%的買家都能上車！</p>

              <p><strong>適用範圍（2026年最新）：</strong></p>
              <ul>
                <li><strong>HKD 400萬或以下：</strong>最高90%按揭</li>
                <li><strong>HKD 400-450萬：</strong>最高80-90%按揭（貸款額上限HKD 360萬）</li>
                <li><strong>HKD 450-600萬：</strong>最高80%按揭</li>
                <li><strong>HKD 600-1,000萬：</strong>最高80%按揭（貸款額上限HKD 500萬）</li>
              </ul>

              <p><strong>保費參考：</strong></p>
              <ul>
                <li>60%按揭：1.15%貸款額</li>
                <li>70%按揭：1.85%貸款額</li>
                <li>80%按揭：2.15%貸款額</li>
                <li>90%按揭：4.35%貸款額（30年期）</li>
              </ul>

              <h3>3. 首次置業印花稅優惠</h3>
              <ul>
                <li>首次買樓可享從價印花稅（AVD）較低稅率</li>
                <li>HKD 300萬樓：印花稅約 HKD 45,000（1.5%）</li>
                <li>HKD 600萬樓：印花稅約 HKD 90,000（1.5%）</li>
                <li>HKD 1,000萬樓：印花稅約 HKD 187,500（1.875%）</li>
                <li>非首次買樓或非香港永久居民：15%重稅！</li>
              </ul>

              <h2>❌ 第五步：避開常見被拒原因</h2>

              <h3>1. 收入不足或不穩定</h3>
              <p><strong>問題：</strong></p>
              <ul>
                <li>月入未能通過壓力測試</li>
                <li>自僱人士收入波動大</li>
                <li>試用期未過</li>
              </ul>
              <p><strong>解決方法：</strong></p>
              <ul>
                <li>✅ 增加擔保人或聯名申請</li>
                <li>✅ 降低貸款額或選擇較平宜物業</li>
                <li>✅ 加長還款年期（減低月供）</li>
                <li>✅ 等待試用期過後再申請</li>
              </ul>

              <h3>2. 信貸評分差</h3>
              <p><strong>問題：</strong></p>
              <ul>
                <li>信用卡逾期還款</li>
                <li>欠債過多</li>
                <li>信貸查詢次數過多</li>
                <li>破產記錄</li>
              </ul>
              <p><strong>解決方法：</strong></p>
              <ul>
                <li>✅ 提前6-12個月改善信貸記錄</li>
                <li>✅ 準時還款，建立良好紀錄</li>
                <li>✅ 還清高息債務</li>
                <li>✅ 向銀行解釋過往不良紀錄的原因</li>
              </ul>

              <h3>3. 首期來源不明</h3>
              <p><strong>問題：</strong></p>
              <ul>
                <li>突然有大額現金入賬</li>
                <li>無法解釋首期來源</li>
              </ul>
              <p><strong>解決方法：</strong></p>
              <ul>
                <li>✅ 提供禮物契（父母資助證明）</li>
                <li>✅ 提供過往儲蓄紀錄</li>
                <li>✅ 提供其他收入證明（如股票沽售紀錄）</li>
              </ul>

              <h3>4. 物業估價不足</h3>
              <p><strong>問題：</strong></p>
              <ul>
                <li>成交價高於銀行估價</li>
                <li>貸款額被削減</li>
              </ul>
              <p><strong>解決方法：</strong></p>
              <ul>
                <li>✅ 同時向多間銀行估價，選擇較高者</li>
                <li>✅ 提供近期相近單位成交紀錄給銀行參考</li>
                <li>✅ 與賣家重新議價</li>
                <li>✅ 準備更多首期</li>
              </ul>

              <h3>5. 樓齡過高</h3>
              <p><strong>問題：</strong></p>
              <ul>
                <li>舊樓（樓齡40年以上）可能批核年期較短</li>
                <li>計算公式：75 - 樓齡 = 最長還款年期（部分銀行）</li>
              </ul>
              <p><strong>解決方法：</strong></p>
              <ul>
                <li>✅ 選擇對樓齡要求較寬鬆的銀行（如80-樓齡）</li>
                <li>✅ 接受較短還款期，但月供會增加</li>
              </ul>

              <h2>💡 第六步：專家貼士助你成功</h2>

              <h3>1. 提前改善財務狀況</h3>
              <ul>
                <li>📅 申請前6-12個月開始準備</li>
                <li>💰 增加儲蓄，減少不必要支出</li>
                <li>💳 減少信用卡使用率至30%以下</li>
                <li>📊 準時還款，建立良好信貸紀錄</li>
                <li>🚫 避免申請新的貸款或信用卡</li>
              </ul>

              <h3>2. 選擇合適的申請時機</h3>
              <ul>
                <li>✅ 試用期過後（通常3-6個月）</li>
                <li>✅ 剛獲加薪或升職（可提高借貸能力）</li>
                <li>✅ 清還其他債務後（減低供款比例）</li>
                <li>✅ 信貸評分提升後</li>
              </ul>

              <h3>3. 善用按揭中介</h3>
              <ul>
                <li>🆓 服務通常免費（銀行支付佣金）</li>
                <li>📊 幫你比較多間銀行條款</li>
                <li>📝 協助準備文件，提高批核率</li>
                <li>🤝 有談判經驗，爭取更優惠條件</li>
                <li>💡 提供專業建議</li>
              </ul>

              <h3>4. 多手準備，提高成功率</h3>
              <ul>
                <li>同時向3-4間銀行申請，比較條件</li>
                <li>準備Plan B（如父母擔保、延長還款期）</li>
                <li>預留額外資金應對突發情況</li>
              </ul>

              <h3>5. 申請期間注意事項</h3>
              <ul>
                <li>❌ 不要離職或轉工</li>
                <li>❌ 不要申請其他貸款</li>
                <li>❌ 不要大額提取存款</li>
                <li>❌ 不要遲交信用卡款</li>
                <li>✅ 保持財務穩定</li>
              </ul>

              <h2>❓ 常見問題 FAQ</h2>

              <h3>Q1: 我剛出來工作1年，可以申請按揭嗎？</h3>
              <p>
                可以，但要看收入是否足夠通過壓力測試。建議：(1) 增加父母作擔保人；(2) 選擇較便宜物業；(3) 申請較長還款期。如果試用期未過，建議等過了試用期再申請，批核機會較高。
              </p>

              <h3>Q2: 自僱人士申請按揭有什麼額外要求？</h3>
              <p>
                銀行會要求2年財務報表（經審計）及報稅表。部分銀行可能只計算平均收入的70-80%。建議找對自僱人士較友善的銀行（如中銀、恒生），並準備詳細的業務收入證明。
              </p>

              <h3>Q3: 壓力測試不合格怎麼辦？</h3>
              <p>
                有幾個方法：(1) 增加擔保人或聯名申請；(2) 降低貸款額；(3) 延長還款期至30年；(4) 申請按揭保險時，部分銀行可豁免壓力測試（但供款比例仍不能超過50%）。
              </p>

              <h3>Q4: 物業估價不足怎麼辦？</h3>
              <p>
                立即向多間銀行估價（可網上估價或找按揭中介），選擇估價較高的銀行申請。同時與賣家協商，看能否減價或你增加首期。最後手段是取消交易（但會損失訂金）。
              </p>

              <h3>Q5: 首期資金不足，有什麼方法？</h3>
              <p>
                (1) 申請按揭保險，最高可借90%；(2) 父母資助（記得簽禮物契）；(3) 購買較便宜物業；(4) 考慮居屋（白表可借90%）；(5) 與伴侶聯名購買，合併資金及收入。
              </p>

              <h3>Q6: 可以用父母名義買樓，我供款嗎？</h3>
              <p>
                可以，但不建議。因為：(1) 你沒有業權，有風險；(2) 你日後自己買樓時，不能享首置優惠；(3) 父母年齡大，可能影響按揭年期。建議用你自己名義買，父母作擔保人更好。
              </p>

              <h3>Q7: 申請幾間銀行會影響信貸評分嗎？</h3>
              <p>
                會有輕微影響，但不大。每次銀行查閱你的信貸報告（TU），都會留下紀錄。建議在2週內完成所有申請，信貸評級機構會視為單一次查詢。避免長時間多次申請。
              </p>

              <h3>Q8: 簽了臨約後，按揭申請被拒怎麼辦？</h3>
              <p>
                這是最壞情況。建議：(1) 立即向其他銀行申請；(2) 找按揭中介幫忙；(3) 增加擔保人；(4) 與賣家協商延期成交。如真的無法批核，可能要放棄交易並損失訂金（通常5%樓價），所以一定要預先獲得按揭預批！
              </p>

              <h3>Q9: 什麼是「罰息期」？</h3>
              <p>
                銀行為防止你獲得現金回贈後立即轉按，會設定罰息期（通常2-3年）。期內如轉按或提早全數還款，需向銀行支付罰款（通常為貸款額1-2%）。不過正常按期供款不受影響。
              </p>

              <h3>Q10: 買一手樓和二手樓，按揭申請有分別嗎？</h3>
              <p>
                主要分別：(1) 一手樓可能有發展商按揭，但條款要小心；(2) 一手樓估價較穩定；(3) 二手樓可能估價不足；(4) 即供vs建期付款，建期按揭風險較高（樓花期間利率可能上升）。
              </p>

              <div style={{
                background: '#dcfce7',
                padding: '25px',
                borderRadius: '12px',
                border: '2px solid #10b981',
                margin: '40px 0',
                textAlign: 'center'
              }}>
                <h2 style={{ marginTop: 0, color: '#065f46' }}>🧮 免費計算你的按揭供款</h2>
                <p style={{ fontSize: '16px', marginBottom: '20px' }}>
                  了解流程後，立即計算你的按揭每月供款、總利息及壓力測試要求！
                </p>
                <a
                  href="/"
                  style={{
                    display: 'inline-block',
                    padding: '14px 32px',
                    backgroundColor: '#10b981',
                    color: 'white',
                    textDecoration: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    fontSize: '16px',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#059669'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#10b981'}
                >
                  開始免費計算 →
                </a>
              </div>

              <h2>🎯 總結：成功置業的關鍵</h2>

              <div style={{
                background: '#f8fafc',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                margin: '20px 0'
              }}>
                <h3 style={{ marginTop: 0 }}>✨ 7大成功要訣</h3>
                <ol>
                  <li><strong>提前規劃：</strong>至少6-12個月開始準備財務</li>
                  <li><strong>充足首期：</strong>最少準備15-20%樓價（包括雜費）</li>
                  <li><strong>穩定收入：</strong>確保能通過壓力測試</li>
                  <li><strong>良好信貸：</strong>維持580分以上TU評分</li>
                  <li><strong>完整文件：</strong>預先準備所有所需文件</li>
                  <li><strong>預先批核：</strong>睇樓前取得按揭預批</li>
                  <li><strong>專業協助：</strong>善用按揭中介免費服務</li>
                </ol>
              </div>

              <p>
                首次置業是人生大事，按揭申請看似複雜，但只要準備充足、了解流程、避開常見錯誤，成功並不困難。記住，預先準備是關鍵！
              </p>

              <p>
                祝你置業順利，早日成為業主！🏠🎉
              </p>

              <div style={{
                background: '#fef3c7',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #fbbf24',
                margin: '30px 0'
              }}>
                <h3 style={{ marginTop: 0 }}>⚠️ 重要聲明</h3>
                <p style={{ fontSize: '14px', marginBottom: 0 }}>
                  本文資訊僅供參考，不構成任何財務或法律建議。按揭政策、利率及條款會隨時變化，申請前請向銀行、按揭中介或專業人士查詢最新資訊。每個人的財務狀況不同，請根據自身情況作出決策。
                </p>
              </div>

              <AmazonRecommendation isEnglish={isEnglish} />

              <div style={{ marginTop: '50px', paddingTop: '30px', borderTop: '1px solid #e2e8f0' }}>
                <p style={{ fontSize: '14px', color: '#64748b' }}>
                  <strong>關於PayWise365：</strong><br />
                  我們提供免費的按揭及貸款計算工具，助你作出明智的財務決策。無需註冊，即時使用，保護你的私隱。
                </p>
                <p style={{ fontSize: '14px', color: '#64748b' }}>
                  <strong>作者：</strong>PayWise365 編輯團隊<br />
                  <strong>發布日期：</strong>2026年1月31日<br />
                  <strong>最後更新：</strong>2026年1月31日
                </p>
              </div>
            </>
          ) : (
            // English Content
            <>
              <div style={{ marginBottom: '30px', paddingBottom: '20px', borderBottom: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '10px' }}>
                  <span style={{
                    background: '#eff6ff',
                    color: '#2563eb',
                    padding: '6px 14px',
                    borderRadius: '14px',
                    fontWeight: '500',
                    marginRight: '12px'
                  }}>
                    First-Time Buyer
                  </span>
                  <span>Published: January 31, 2026</span>
                  <span style={{ margin: '0 8px' }}>•</span>
                  <span>Reading Time: 15 minutes</span>
                </div>
              </div>

              <div style={{
                background: '#dcfce7',
                padding: '25px',
                borderRadius: '12px',
                border: '2px solid #10b981',
                margin: '40px 0',
                textAlign: 'center'
              }}>
                <h2 style={{ marginTop: 0, color: '#065f46' }}>🧮 Calculate Your Mortgage</h2>
                <p style={{ fontSize: '16px', marginBottom: '20px' }}>
                  Use our free calculator to estimate your monthly payments and affordability before applying for a mortgage.
                </p>
                <a
                  href="/"
                  style={{
                    display: 'inline-block',
                    padding: '14px 32px',
                    backgroundColor: '#10b981',
                    color: 'white',
                    textDecoration: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    fontSize: '16px',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#059669'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#10b981'}
                >
                  Calculate Now →
                </a>
              </div>

              <h2>🏠 Complete First-Time Homebuyer Guide: Hong Kong 2026</h2>
              <p>
                Buying your first property in Hong Kong is an exciting but complex journey. This comprehensive guide covers everything you need to know: mortgage application, stress testing, down payment calculation, government schemes, and more. Let's help you become a proud homeowner!
              </p>

              <h2>💰 Step 1: Calculate Your Budget</h2>

              <h3>How Much Can You Afford?</h3>
              <p>Use the <strong>28/36 Rule</strong> as a starting guideline:</p>
              <ul>
                <li><strong>28% Rule:</strong> Monthly mortgage payment should not exceed 28% of your monthly gross income</li>
                <li><strong>36% Rule:</strong> Total debt repayment (including mortgage) should not exceed 36% of monthly income</li>
              </ul>

              <h3>Example: HKD 50,000 Monthly Income</h3>
              <ul>
                <li>Maximum mortgage payment: HKD 14,000 (28%)</li>
                <li>Maximum total debt payment: HKD 18,000 (36%)</li>
                <li>At 4% interest rate, 30-year term: Can afford ~HKD 3M loan</li>
                <li>With 10% down payment: Can buy property worth ~HKD 3.3M</li>
              </ul>

              <h2>📊 Step 2: Understand the Stress Test</h2>

              <p>The HKMA stress test requires you to prove affordability even if interest rates rise by 3%:</p>

              <div style={{
                background: '#f0f9ff',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #7dd3fc',
                margin: '20px 0'
              }}>
                <h3 style={{ marginTop: 0 }}>Stress Test Formula:</h3>
                <p><strong>(Mortgage Rate + 3%) × Loan Amount ÷ 12 ÷ Monthly Income ≤ 50%</strong></p>

                <h4>Example: HKD 4M Loan at 4% Interest</h4>
                <ul>
                  <li>Normal payment at 4%: HKD 19,112/month</li>
                  <li>Stress test at 7% (4% + 3%): HKD 26,634/month</li>
                  <li><strong>Required monthly income: HKD 53,268 minimum</strong></li>
                </ul>
              </div>

              <h2>💵 Step 3: Save for Down Payment</h2>

              <h3>Standard LTV Ratios (2026)</h3>
              <ul>
                <li><strong>Property under HKD 10M:</strong> 90% LTV (10% down payment) with HKMC insurance</li>
                <li><strong>Property HKD 10-15M:</strong> 80-90% LTV with insurance</li>
                <li><strong>Property over HKD 15M:</strong> 70% LTV maximum (30% down payment)</li>
              </ul>

              <h3>Additional Costs Beyond Down Payment:</h3>
              <div style={{
                background: '#f8fafc',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                margin: '20px 0'
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                  <thead>
                    <tr style={{ background: '#eff6ff' }}>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #2563eb' }}>Item</th>
                      <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #2563eb' }}>% of Property Price</th>
                      <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #2563eb' }}>Example (HKD 5M Property)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>Down Payment (10%)</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>10%</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0', fontWeight: 'bold' }}>HKD 500,000</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>Stamp Duty</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>~4.25%</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>HKD 212,500</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>Legal Fees</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>~0.4%</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>HKD 20,000</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>Mortgage Insurance (if applicable)</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>2-5%</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>HKD 90,000-180,000</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>Agent Commission</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>1%</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>HKD 50,000</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px' }}><strong>TOTAL CASH NEEDED</strong></td>
                      <td style={{ padding: '12px', textAlign: 'right' }}><strong>~17-20%</strong></td>
                      <td style={{ padding: '12px', textAlign: 'right', fontWeight: 'bold', color: '#2563eb' }}>HKD 872,500-962,500</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2>🏦 Step 4: Mortgage Application Process</h2>

              <div style={{
                background: '#f8fafc',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                margin: '20px 0'
              }}>
                <h3 style={{ marginTop: 0 }}>Timeline: 4-6 Weeks</h3>
                <ol>
                  <li><strong>Week 1:</strong> Submit application & documents to 2-3 banks</li>
                  <li><strong>Week 2-3:</strong> Bank property valuation & document verification</li>
                  <li><strong>Week 3-4:</strong> Mortgage approval (conditional or final)</li>
                  <li><strong>Week 4-6:</strong> Sign mortgage contract & drawdown</li>
                </ol>
              </div>

              <h3>Required Documents:</h3>
              <ul>
                <li>✅ HKID card (both sides)</li>
                <li>✅ 3-6 months payslips</li>
                <li>✅ 3-6 months bank statements</li>
                <li>✅ Employment letter/contract</li>
                <li>✅ Provisional Sale & Purchase Agreement</li>
                <li>✅ Tax assessment (if applicable)</li>
              </ul>

              <h2>🎯 Government Schemes for First-Time Buyers</h2>

              <h3>1. Home Ownership Scheme (HOS)</h3>
              <ul>
                <li><strong>Price:</strong> 30-50% below market value</li>
                <li><strong>Eligibility:</strong> Income limits apply (HKD 66,000/month for families)</li>
                <li><strong>Mortgage:</strong> Up to 90-95% LTV, lower interest rates</li>
                <li><strong>Restriction:</strong> Cannot resell freely for 5 years</li>
              </ul>

              <h3>2. Starter Homes (New Policy 2026)</h3>
              <ul>
                <li><strong>Price:</strong> Capped below market rates</li>
                <li><strong>Size:</strong> Smaller units (200-450 sq ft)</li>
                <li><strong>Location:</strong> New Territories mainly</li>
                <li><strong>Benefit:</strong> More affordable entry point</li>
              </ul>

              <h3>3. Green Form Subsidized Home Ownership Scheme</h3>
              <ul>
                <li><strong>Who:</strong> Public housing tenants, Green Form holders</li>
                <li><strong>Benefit:</strong> Most heavily subsidized, lowest prices</li>
                <li><strong>Application:</strong> Through Housing Authority</li>
              </ul>

              <h2>💡 Expert Tips for First-Time Buyers</h2>

              <div style={{
                background: '#eff6ff',
                padding: '25px',
                borderRadius: '12px',
                border: '2px solid #2563eb',
                margin: '30px 0'
              }}>
                <h3 style={{ marginTop: 0, color: '#1e40af' }}>🌟 Golden Rules</h3>
                <ol>
                  <li>
                    <strong>Start Saving Early</strong><br />
                    <span style={{ fontSize: '14px', color: '#64748b' }}>
                      → Aim for 20-25% of property price for down payment + fees
                    </span>
                  </li>
                  <li>
                    <strong>Check Your Credit Score</strong><br />
                    <span style={{ fontSize: '14px', color: '#64748b' }}>
                      → Clear all credit card debts, maintain good payment history
                    </span>
                  </li>
                  <li>
                    <strong>Shop Around for Mortgages</strong><br />
                    <span style={{ fontSize: '14px', color: '#64748b' }}>
                      → Apply to 2-3 banks, compare rates and terms
                    </span>
                  </li>
                  <li>
                    <strong>Don't Max Out Your Budget</strong><br />
                    <span style={{ fontSize: '14px', color: '#64748b' }}>
                      → Leave room for emergencies, renovations, furniture
                    </span>
                  </li>
                  <li>
                    <strong>Consider Future Needs</strong><br />
                    <span style={{ fontSize: '14px', color: '#64748b' }}>
                      → Family growth, career changes, resale value
                    </span>
                  </li>
                </ol>
              </div>

              <h2>❓ Common First-Time Buyer Questions</h2>

              <div style={{ background: '#f8fafc', padding: '15px', borderRadius: '8px', marginBottom: '15px', border: '1px solid #e2e8f0' }}>
                <h4 style={{ marginTop: 0, color: '#2563eb' }}>Q: Should I buy alone or with a partner?</h4>
                <p style={{ marginBottom: 0 }}>
                  <strong>Joint mortgage pros:</strong> Higher borrowing capacity, shared financial burden, easier to pass stress test.<br />
                  <strong>Cons:</strong> Both parties tied to mortgage, complications if relationship ends, both credit scores affected.<br />
                  <strong>Consider:</strong> Legal agreements (e.g., % ownership), exit strategy, long-term commitment.
                </p>
              </div>

              <div style={{ background: '#f8fafc', padding: '15px', borderRadius: '8px', marginBottom: '15px', border: '1px solid #e2e8f0' }}>
                <h4 style={{ marginTop: 0, color: '#2563eb' }}>Q: New property vs. Second-hand property?</h4>
                <p style={{ marginBottom: 0 }}>
                  <strong>New property:</strong> No stamp duty for first-time buyers (certain conditions), modern facilities, warranty, longer wait time.<br />
                  <strong>Second-hand:</strong> Immediate occupancy, established neighborhood, potentially better value, full stamp duty applies.<br />
                  <strong>Recommendation:</strong> Calculate total costs including stamp duty before deciding.
                </p>
              </div>

              <div style={{ background: '#f8fafc', padding: '15px', borderRadius: '8px', marginBottom: '15px', border: '1px solid #e2e8f0' }}>
                <h4 style={{ marginTop: 0, color: '#2563eb' }}>Q: How much should I spend on renovation?</h4>
                <p style={{ marginBottom: 0 }}>
                  <strong>Budget guideline:</strong> HKD 300-600 per sq ft for basic renovation, HKD 600-1,000+ for premium.<br />
                  <strong>For 400 sq ft unit:</strong> HKD 120,000-240,000 basic, HKD 240,000-400,000 premium.<br />
                  <strong>Tip:</strong> Start with essentials, phase renovations over time to spread costs.
                </p>
              </div>

              <h2>🧮 Use Our Free Mortgage Calculator</h2>
              <div style={{
                background: '#dcfce7',
                padding: '25px',
                borderRadius: '12px',
                border: '2px solid #10b981',
                margin: '40px 0',
                textAlign: 'center'
              }}>
                <h3 style={{ marginTop: 0, color: '#065f46' }}>💚 Plan Your First Home Purchase</h3>
                <p style={{ fontSize: '16px', marginBottom: '20px' }}>
                  Calculate monthly payments, stress test requirements, and total costs for your first Hong Kong property.
                </p>
                <a
                  href="/"
                  style={{
                    display: 'inline-block',
                    padding: '14px 32px',
                    backgroundColor: '#10b981',
                    color: 'white',
                    textDecoration: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    fontSize: '16px',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#059669'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#10b981'}
                >
                  Start Planning Now →
                </a>
              </div>

              <p style={{ fontSize: '14px', color: '#64748b', fontStyle: 'italic', marginTop: '40px' }}>
                💡 <strong>Note:</strong> This is an abbreviated English version of our comprehensive first-time homebuyer guide. For the complete guide with detailed sections on property hunting, negotiation strategies, and post-purchase tips, switch to Traditional Chinese (繁體中文) using the language toggle.
              </p>

              <AmazonRecommendation isEnglish={isEnglish} />

              <div style={{ marginTop: '50px', paddingTop: '30px', borderTop: '1px solid #e2e8f0' }}>
                <p style={{ fontSize: '14px', color: '#64748b' }}>
                  <strong>About PayWise365:</strong><br />
                  We provide free mortgage and loan calculation tools to help Hong Kong residents make informed financial decisions. No registration required, instant results, complete privacy.
                </p>
                <p style={{ fontSize: '14px', color: '#64748b' }}>
                  <strong>Author:</strong> PayWise365 Editorial Team<br />
                  <strong>Published:</strong> January 31, 2026<br />
                  <strong>Last Updated:</strong> January 31, 2026
                </p>
              </div>
            </>
          )}
        </Box>
      </Container>
    </Box>
  );
}
